import React, { useEffect, useRef, useState, ReactNode } from "react";
import { Animated, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { Feather } from "@expo/vector-icons";
import { colors } from "../../../theme";

const redDark = colors.red.dark;

const DURATION = 400;
const INITIAL_TOP = 0;

export interface Props {
  errorTitle: string;
  errorAlert: boolean;
  errorMessage?: string | ReactNode;
  onCloseErrorMessage(): void;
}

ErrorMessage.defaultProps = {
  errorTitle: "An error occurred",
  errorAlert: false,
};

enum Visibility {
  HIDDEN = "HIDDEN",
  HIDING = "HIDING",
  REVEALING = "REVEALING",
  VISIBLE = "VISIBLE",
}

const changeVisibility =
  ({ to, unless }: { to: Visibility; unless: Visibility }) =>
  (prev: Visibility) =>
    prev === unless ? unless : to;

function ErrorMessage({ errorAlert, errorMessage, errorTitle, onCloseErrorMessage }: Props) {
  const [visibility, setVisibility] = useState(errorMessage ? Visibility.REVEALING : Visibility.HIDDEN);
  const [message, setMessage] = useState(errorMessage);
  const [contentHeight, setContentHeight] = useState(0);
  const animation = useRef(new Animated.Value(INITIAL_TOP)).current;

  function onPressClose() {
    onCloseErrorMessage();
    setVisibility(Visibility.HIDING);
  }

  useEffect(() => {
    if (errorMessage) {
      setMessage(errorMessage);
      setVisibility(changeVisibility({ to: Visibility.REVEALING, unless: Visibility.HIDING }));
    } else {
      setVisibility(changeVisibility({ to: Visibility.HIDING, unless: Visibility.HIDDEN }));
    }
  }, [errorMessage, onCloseErrorMessage]);

  useEffect(() => {
    let timeouts: NodeJS.Timeout[] = [];

    function animate(toValue: number, targetVisibility: Visibility) {
      Animated.timing(animation, { toValue, duration: DURATION, useNativeDriver: true }).start();

      const timeout = setTimeout(() => {
        setVisibility(targetVisibility);
        timeouts = timeouts.filter((t) => t !== timeout);
      }, DURATION);
    }

    switch (visibility) {
      case Visibility.HIDING:
        animate(INITIAL_TOP, Visibility.HIDDEN);
        break;
      case Visibility.REVEALING:
        animate(-contentHeight, Visibility.VISIBLE);
        break;
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [visibility, contentHeight]);

  return (
    <>
      {visibility !== Visibility.HIDDEN && (
        <Animated.View style={[styles.view, { transform: [{ translateY: animation }] }]}>
          <View
            className="border-t border-sky bg-sky-white"
            onLayout={(event) => {
              const { height } = event.nativeEvent.layout;
              setContentHeight(height);
            }}
          >
            {/* Web hack: Adding a large margin to negate the negative margin in ActionsContainer */}
            <View className="mx-6 sm:mx-14">
              <View className="flex-row pt-4">
                <View className="flex-row flex-1 items-center">
                  {errorAlert && <Feather name="alert-triangle" color={redDark} size={24} />}
                  <Text className={`text-small-tight font-700 ${errorAlert ? "text-red-dark ml-2" : "text-ink-dark"}`}>
                    {errorTitle}
                  </Text>
                </View>
                <Feather name="x" size={24} onPress={onPressClose} testID="close-icon" />
              </View>
              {typeof message === "string" ? (
                <Text className="text-ink text-small-tight font-400 pt-2 pb-4" numberOfLines={3}>
                  {message}
                </Text>
              ) : (
                <View className="pt-2 pb-4">{message}</View>
              )}
            </View>
          </View>
        </Animated.View>
      )}
    </>
  );
}

export default ErrorMessage;

const styles = StyleSheet.create({
  view: {
    position: "absolute",
    left: 0,
    right: 0,
  },
});
